import { useEffect, useRef, useState } from "react";
import type { DocumentType } from "../api";
import { deleteType, learnedAliasStats } from "../api";

type Props = {
  types: DocumentType[];
  loading: boolean;
  onChanged: () => void;
  onOpenManager: () => void;
};

/**
 * Top-nav dropdown "Config": overzicht van beide DB-gestuurde configuratie-bronnen —
 * getrainde documenttypes én runtime-geleerde label-aliassen (zie fase A). Hoofd-badge
 * telt de som zodat je in één oogopslag ziet of de set is uitgebreid sinds vorige keer.
 *
 * Editing-flow leeft in een aparte modal (fase C) — hier staat alleen de inkijk en
 * de "Beheren…"-knop die 'em opent.
 */
export function ConfigDropdown({ types, loading, onChanged, onOpenManager }: Props) {
  const [open, setOpen] = useState(false);
  const [aliasStats, setAliasStats] = useState<Record<string, number>>({});
  const [aliasLoading, setAliasLoading] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  // Alias-stats pas bij openen laden — niet elke render de API aantikken.
  useEffect(() => {
    if (!open) return;
    setAliasLoading(true);
    learnedAliasStats()
      .then((s) => setAliasStats(s))
      .catch(() => setAliasStats({}))
      .finally(() => setAliasLoading(false));
  }, [open]);

  const handleDelete = async (id: number, naam: string) => {
    if (!confirm(`Type '${naam}' verwijderen?`)) return;
    await deleteType(id);
    onChanged();
  };

  const totalAliases = Object.values(aliasStats).reduce((a, b) => a + b, 0);
  // Badge-tally: types + geleerde aliassen samen, zodat één getal de hele
  // Config-staat weergeeft. Tijdens eerste render (voor open) tonen we alleen
  // types-count — alias-stats worden on-demand geladen.
  const badge = open ? types.length + totalAliases : types.length;

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((s) => !s)}
        className="flex items-center gap-2 rounded border bg-white px-3 py-2 text-sm hover:bg-gray-50"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span>Config</span>
        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-mono text-gray-600">
          {loading ? "…" : badge}
        </span>
        <svg
          className={`w-4 h-4 text-gray-500 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-[28rem] origin-top-right rounded-lg border bg-white shadow-lg z-20">
          <div className="max-h-[32rem] overflow-y-auto">
            {/* ─── Types ─────────────────────────────────────────────── */}
            <div className="px-3 pt-3 pb-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
              Getrainde types
              <span className="ml-2 font-mono text-gray-400">
                {loading ? "…" : types.length}
              </span>
            </div>
            <div className="p-2">
              {loading && <div className="p-2 text-xs text-gray-500">Laden…</div>}
              {!loading && types.length === 0 && (
                <div className="p-2 text-xs text-gray-500">
                  Nog geen types. Upload een document om te beginnen.
                </div>
              )}
              <ul className="space-y-1">
                {types.map((t) => (
                  <li
                    key={t.id}
                    className="group flex items-start justify-between gap-2 rounded p-2 text-sm hover:bg-gray-50"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="font-medium truncate">{t.naam}</div>
                      {t.afzender && (
                        <div className="text-xs text-gray-500 truncate">{t.afzender}</div>
                      )}
                      <div className="text-xs text-gray-400 mt-0.5">
                        {t.categorie || "?"} · {t.taal || "?"} ·{" "}
                        {t.fingerprint.anchors.length} anchors ·{" "}
                        <span className="font-mono">{t.gebruik_count}×</span>
                      </div>
                    </div>
                    <button
                      onClick={() => handleDelete(t.id, t.naam)}
                      className="opacity-0 group-hover:opacity-100 text-xs text-red-500 hover:text-red-700 px-1"
                      title="Verwijderen"
                    >
                      ×
                    </button>
                  </li>
                ))}
              </ul>
            </div>

            {/* ─── Geleerde labels ────────────────────────────────────── */}
            <div className="border-t px-3 pt-3 pb-1 text-xs font-medium text-gray-500 uppercase tracking-wide">
              Geleerde labels
              <span className="ml-2 font-mono text-gray-400">
                {aliasLoading ? "…" : totalAliases}
              </span>
            </div>
            <div className="p-2">
              {aliasLoading && <div className="p-2 text-xs text-gray-500">Laden…</div>}
              {!aliasLoading && totalAliases === 0 && (
                <div className="p-2 text-xs text-gray-500">
                  Nog geen label-varianten geleerd. Komen vanzelf binnen bij
                  OCR-typo's of nieuwe talen.
                </div>
              )}
              {!aliasLoading && totalAliases > 0 && (
                <ul className="space-y-1 text-sm">
                  {Object.entries(aliasStats)
                    .sort(([a], [b]) => a.localeCompare(b))
                    .map(([doctype, count]) => (
                      <li
                        key={doctype}
                        className="flex items-center justify-between p-2 rounded hover:bg-gray-50"
                      >
                        <span className="capitalize">{doctype.replace(/_/g, " ")}</span>
                        <span className="font-mono text-xs text-gray-500">{count}</span>
                      </li>
                    ))}
                </ul>
              )}
            </div>
          </div>

          {/* ─── Beheren-knop ─────────────────────────────────────────── */}
          <div className="border-t p-2">
            <button
              onClick={() => {
                setOpen(false);
                onOpenManager();
              }}
              className="w-full rounded bg-blue-50 px-3 py-2 text-sm text-blue-700 hover:bg-blue-100"
            >
              Beheren…
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
